import { supabase } from '../supabase/client';

export interface QuestionStat {
  questionId: string;
  paperId: string;
  totalAttempts: number;
  correctAttempts: number;
  wrongAttempts: number;
  correctRate: number;
  autoDifficulty: 'easy' | 'medium' | 'hard';
  lastCalculatedAt: string;
}

export interface UserQuestionRecord {
  questionId: string;
  paperId: string;
  isCorrect: boolean;
  answer: string;
  answeredAt: string;
}

export async function getQuestionStats(): Promise<Record<string, QuestionStat>> {
  const { data, error } = await supabase.from('question_stats').select('*');
  if (error) {
    
    return {};
  }
  const stats: Record<string, QuestionStat> = {};
  data?.forEach((row: any) => {
    const key = `${row.paper_id}_${row.question_id}`;
    stats[key] = {
      questionId: row.question_id,
      paperId: row.paper_id,
      totalAttempts: row.total_attempts || 0,
      correctAttempts: row.correct_attempts || 0,
      wrongAttempts: row.wrong_attempts || 0,
      correctRate: parseFloat(row.correct_rate) || 0,
      autoDifficulty: row.auto_difficulty || 'medium',
      lastCalculatedAt: row.last_calculated_at,
    };
  });
  return stats;
}

export async function getQuestionStat(questionId: string, paperId: string): Promise<QuestionStat | null> {
  const { data, error } = await supabase
    .from('question_stats')
    .select('*')
    .eq('question_id', questionId)
    .eq('paper_id', paperId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    questionId: data.question_id,
    paperId: data.paper_id,
    totalAttempts: data.total_attempts || 0,
    correctAttempts: data.correct_attempts || 0,
    wrongAttempts: data.wrong_attempts || 0,
    correctRate: parseFloat(String(data.correct_rate)) || 0,
    autoDifficulty: (data.auto_difficulty as any) || 'medium',
    lastCalculatedAt: data.last_calculated_at,
  };
}

export function calculateDifficulty(correctRate: number): 'easy' | 'medium' | 'hard' {
  if (correctRate >= 70) return 'easy';
  if (correctRate >= 30) return 'medium';
  return 'hard';
}

export async function updateQuestionStat(
  questionId: string,
  paperId: string,
  isCorrect: boolean
): Promise<QuestionStat | null> {
  const existing = await getQuestionStat(questionId, paperId);
  const totalAttempts = (existing?.totalAttempts || 0) + 1;
  const correctAttempts = (existing?.correctAttempts || 0) + (isCorrect ? 1 : 0);
  const wrongAttempts = (existing?.wrongAttempts || 0) + (isCorrect ? 0 : 1);
  const correctRate = totalAttempts > 0 ? Math.round((correctAttempts / totalAttempts) * 100) : 0;
  const autoDifficulty = calculateDifficulty(correctRate);
  const lastCalculatedAt = new Date().toISOString();

  const { error } = await supabase.from('question_stats').upsert({
    question_id: questionId,
    paper_id: paperId,
    total_attempts: totalAttempts,
    correct_attempts: correctAttempts,
    wrong_attempts: wrongAttempts,
    correct_rate: correctRate,
    auto_difficulty: autoDifficulty,
    last_calculated_at: lastCalculatedAt,
  }, { onConflict: 'question_id,paper_id' });

  if (error) {
    
    return null;
  }

  return {
    questionId,
    paperId,
    totalAttempts,
    correctAttempts,
    wrongAttempts,
    correctRate,
    autoDifficulty,
    lastCalculatedAt,
  };
}

export async function getQuestionsDifficulty(
  questionIds: string[],
  paperId?: string
): Promise<Record<string, 'easy' | 'medium' | 'hard'>> {
  if (!questionIds || questionIds.length === 0) return {};

  const result: Record<string, 'easy' | 'medium' | 'hard'> = {};
  // 分批查询，避免 URL 过长（每批 100 个 UUID，约 3.6KB，远低于浏览器/服务器限制）
  const BATCH_SIZE = 100;
  const batches: string[][] = [];
  for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
    batches.push(questionIds.slice(i, i + BATCH_SIZE));
  }

  // 串行执行各批次（避免并发请求过多）
  for (const batch of batches) {
    let query = supabase
      .from('question_stats')
      .select('question_id, auto_difficulty')
      .in('question_id', batch);
    // 仅当 paperId 是有效 UUID 时才加过滤；'all' 等非 UUID 值会导致查询失败
    if (paperId && paperId !== 'all' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(paperId)) {
      query = query.eq('paper_id', paperId);
    }
    const { data, error } = await query;
    if (error) continue;
    data?.forEach((row: any) => {
      result[row.question_id] = row.auto_difficulty || 'medium';
    });
  }
  return result;
}

export async function getUserQuestionRecords(userId: string): Promise<UserQuestionRecord[]> {
  const { data, error } = await supabase
    .from('user_question_records')
    .select('*')
    .eq('user_id', userId);
  if (error) {
    
    return [];
  }
  return data?.map((row: any) => ({
    questionId: row.question_id,
    paperId: row.paper_id,
    isCorrect: row.is_correct,
    answer: row.answer || '',
    answeredAt: row.answered_at,
  })) || [];
}

export async function saveUserQuestionRecord(
  userId: string,
  record: UserQuestionRecord
): Promise<boolean> {
  const { error } = await supabase.from('user_question_records').upsert({
    user_id: userId,
    question_id: record.questionId,
    paper_id: record.paperId,
    is_correct: record.isCorrect,
    answer: record.answer,
    answered_at: record.answeredAt,
  }, { onConflict: 'user_id,question_id,paper_id' });
  if (error) {
    
    return false;
  }
  return true;
}

export async function getUserWrongRecords(userId: string): Promise<UserQuestionRecord[]> {
  const records = await getUserQuestionRecords(userId);
  return records.filter(r => !r.isCorrect);
}

export async function isQuestionWrong(
  userId: string,
  questionId: string,
  paperId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_question_records')
    .select('is_correct')
    .eq('user_id', userId)
    .eq('question_id', questionId)
    .eq('paper_id', paperId)
    .maybeSingle();
  if (error || !data) return false;
  return !data.is_correct;
}

export async function getUserWrongQuestionIds(userId: string): Promise<Set<string>> {
  const wrongRecords = await getUserWrongRecords(userId);
  return new Set(wrongRecords.map(r => r.questionId));
}

export async function countUserWrongQuestions(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('user_question_records')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_correct', false);
  if (error) return 0;
  return count || 0;
}

// ============ 首次答题记录（用于统计题目整体正确率） ============

/**
 * 批量记录学员对多道题的首次答题结果
 * 利用 UNIQUE(question_id, user_id) 约束：已记录过的题目会被忽略（onConflict ignore）
 * 仅客观题（单选/多选/判断）会调用此函数，主观题由教师评分后另行处理
 */
export async function saveFirstAttempts(
  userId: string,
  attempts: Array<{ questionId: string; isCorrect: boolean; userAnswer?: string }>
): Promise<void> {
  if (!userId || attempts.length === 0) return;
  const rows = attempts.map(a => ({
    question_id: a.questionId,
    user_id: userId,
    is_correct: a.isCorrect,
    user_answer: a.userAnswer || null,
  }));
  // onConflict ignore：已存在记录的题目跳过，确保只记录"首次"
  const { error } = await supabase
    .from('question_first_attempts')
    .upsert(rows, { onConflict: 'question_id,user_id', ignoreDuplicates: true });
  if (error) {
    console.error('保存首次答题记录失败:', error.message);
  }
}

/**
 * 题目正确率数据（含答题人数）
 */
export interface QuestionCorrectRateInfo {
  rate: number; // 正确率百分比 0-100
  count: number; // 首次答题总人数
}

/**
 * 批量查询题目正确率（基于全体学员首次答题数据）
 * 返回 Record<questionId, { rate, count }>
 * 使用 RPC 函数 get_question_correct_rates，通过 POST JSON body 传递 question_ids 数组
 * 避免 GET in.() 查询 URL 过长导致 502 Bad Gateway
 */
export async function getQuestionsCorrectRate(
  questionIds: string[]
): Promise<Record<string, QuestionCorrectRateInfo>> {
  if (!questionIds || questionIds.length === 0) return {};

  const result: Record<string, QuestionCorrectRateInfo> = {};

  // RPC 调用：POST JSON body，不受 URL 长度限制
  // 但单次 RPC 仍有性能上限，分批 200 个/次
  const BATCH_SIZE = 200;
  const batches: string[][] = [];
  for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
    batches.push(questionIds.slice(i, i + BATCH_SIZE));
  }

  for (const batch of batches) {
    const { data, error } = await supabase.rpc('get_question_correct_rates', {
      question_ids: batch,
    });
    if (error) {
      console.warn('RPC查询题目正确率失败:', error.message);
      continue;
    }
    (data || []).forEach((row: any) => {
      result[row.question_id] = {
        rate: row.correct_rate || 0,
        count: row.attempt_count || 0,
      };
    });
  }
  return result;
}

/**
 * 根据正确率计算难度等级
 * 简单题：正确率 >= 70%
 * 中等题：30% <= 正确率 < 70%
 * 难题：正确率 < 30%
 * 无数据时默认 'medium'
 */
export function calcDifficultyByCorrectRate(correctRate: number | undefined): 'easy' | 'medium' | 'hard' {
  if (correctRate === undefined) return 'medium';
  if (correctRate >= 70) return 'easy';
  if (correctRate >= 30) return 'medium';
  return 'hard';
}
